/* eslint-disable react-hooks/exhaustive-deps */
import { Column } from '@antv/g2plot';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import { getProgress } from '../helpers';
import { useGeneralContext } from '../../../../context/General';

const colors = [
  '(23, 42, 60',
  '(24, 144,  255',
  '(145, 171, 195',
  '(69, 90, 111',
  '(200, 229, 255',
];

const CustomTooltipContent = (data, t) => {
  if (data[0]) {
    if (data[0].data) {
      const { label, value, lastUpdated, sources } = data[0].data;
      return `
        <div class="custom-tooltip">
          <label class="label mb-2">${label}</label>
          ${sources ? `
            <div class="table">
              <div class="table-header">
                <label class="subjects">${t('Subjects')}</label>
                <label class="report">${t('Report')}%</label>
              </div>
              ${sources.map((s) => `
                <div class="item">
                  <label class="subjects">${s.label}</label>
                  <div class="progress-cont">
                    <div class="progress">
                      <div
                        style="width: ${`${s.contribution}%`}; background: var(--color-primary-70); border-radius: 12px;"
                        ></div>
                    </div>
                    <label class="no-margin-right">${getProgress(s.contribution || '0')}%</label>
                  </div>
                </div>
              `).join('')}
            </div>
          ` : `
            <div class="value mb-1">
              <label class="title">${t('Information Contribution')}</label>
              <label class="percentage">${getProgress(value || 0)}%</label>
            </div>
            <div class="value">
              <label class="title">${t('Last Update')}</label>
              <label>${moment(lastUpdated).format('D MMM YYYY')}</label>
            </div>
          `}
        </div>
      `;
    }
  }
  return '';
};

export const useChartHelper = ({ ref, sources = [] }, t = (s) => s) => {
  const [cont, setCont] = useState(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const { theme } = useGeneralContext();
  const allColors = useMemo(() => {
    let newColors = [];
    let j = 0;
    sources.forEach(() => {
      newColors.push(colors[j]);
      j += 1;
      if (j === colors.length) {
        j = 0;
      }
    });
    return newColors;
  }, [sources]);

  const data = useMemo(() => {
    if (sources.length) {
      const items = sources.map((p, i) => ({
        source: i,
        label: p.label,
        value: p.contribution || 0,
        lastUpdated: p.lastUpdated,
        sources: p.sources,
      }));
      return items;
    }
    return [];
  }, [sources]);

  const createChart = (callback) => {
    const area = new Column(ref.current, {
      autoFit: true,
      data,
      xField: 'source',
      yField: 'value',
      xAxis: {
        label: {
          formatter: () => '',
        },
        nice: true,
      },
      startOnZero: false,
      yAxis: {
        tickMethod: () => {
          return [0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100];
        },
        label: {
          formatter: (val) => `${val}%`,
        },
        nice: true,
      },
      tooltip: {
        customContent: (a, d) => {
          if (a) {
            return CustomTooltipContent(d, t);
          }
          return '';
        },
        showCrosshairs: false,
      },
      point: {
        size: 3,
        shape: 'circle',
        style: (a) => {
          return {
            fill: a.time === '0' ? 'transparent' : 'white',
            stroke: a.time === '0' ? 'transparent' : theme?.mmtPrimary7
          }
        },
      },
      color: (val) => {
        return `rgba${allColors[val.source] || ''}, 1)`;
      },
      animation: false,
    });
    area.render();
    callback(area);
  };

  useEffect(() => {
    if (cont) {
      if (typeof activeIndex === 'number') {
        cont.update({
          color: (val) => {
            const opacity = val.source === activeIndex ? 1 : 0.5;
            return `rgba${allColors[val.source] || ''}, ${opacity})`;
          },
        });
      } else {
        cont.update({
          color: (val) => {
            return `rgba${allColors[val.source] || ''}, 1)`;
          },
        })
      }
    }
  }, [activeIndex]);

  useEffect(() => {
    if (cont) {
      cont.update({
        data,
        tooltip: {
          customContent: (a, d) => {
            if (a) {
              return CustomTooltipContent(d, t);
            }
            return '';
          }
        },
      });
    } else {
      createChart((c) => setCont(c));
    }
  }, [data, t]);

  return {
    activeIndex,
    setActiveIndex,
  };
};
